import { Cross2Icon } from '@radix-ui/react-icons';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProjects } from '@/features/projects/shared/hooks/use-projects';
import SearchIcon from '@/shared/assets/magnifyingglass.svg?react';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { Switch } from '@/shared/components/ui/switch';
import { getInitials } from '@/shared/utils/avatar-util';

interface ProjectSelectorPopoverProps {
  selectedProjectId: number | null;
  onProjectSelect: (projectId: number | null) => void;
  onActiveChange?: (isActive: boolean) => void;
  isActive?: boolean;
  trigger?: React.ReactNode;
  child: React.ReactNode;
}

export const ProjectSelectorPopover = ({
  selectedProjectId,
  onProjectSelect,
  onActiveChange,
  isActive: initialIsActive = true,
  trigger,
  child,
}: ProjectSelectorPopoverProps) => {
  const { t } = useTranslation('overview');
  const { t: tCategories } = useTranslation('categories');
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelectedProjectId, setTempSelectedProjectId] = useState<
    number | null
  >(selectedProjectId);
  const [isActive, setIsActive] = useState(initialIsActive);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: projects = [] } = useProjects();

  const isEditMode = selectedProjectId !== null;

  useEffect(() => {
    setTempSelectedProjectId(selectedProjectId);
  }, [selectedProjectId]);

  useEffect(() => {
    setIsActive(initialIsActive);
  }, [initialIsActive]);

  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    const query = searchQuery.toLowerCase();
    return projects.filter((project) =>
      project.name.toLowerCase().includes(query),
    );
  }, [projects, searchQuery]);

  const handleActiveChange = (newIsActive: boolean) => {
    setIsActive(newIsActive);
  };

  const handleApply = () => {
    onProjectSelect(tempSelectedProjectId);
    if (onActiveChange && isActive !== initialIsActive) {
      onActiveChange(isActive);
    }
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempSelectedProjectId(null);
    onProjectSelect(null);
    setIsOpen(false);
  };

  const handleClose = () => {
    setTempSelectedProjectId(selectedProjectId);
    setIsActive(initialIsActive);
    setSearchQuery('');
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTempSelectedProjectId(selectedProjectId);
      setIsActive(initialIsActive);
      setSearchQuery('');
    }
  };

  const isProjectSelected = !!tempSelectedProjectId;
  const hasChanges =
    tempSelectedProjectId !== selectedProjectId ||
    (selectedProjectId && !tempSelectedProjectId) ||
    isActive !== initialIsActive;

  const tempSelectedProject = projects.find(
    (project) => project.id === tempSelectedProjectId,
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      {trigger && (
        <div className="relative size-full">
          <PopoverTrigger className="size-full">{trigger}</PopoverTrigger>
          <PopoverAnchor className="pointer-events-none absolute left-1/2 top-1/2" />
        </div>
      )}
      <PopoverContent
        className="w-[400px] max-w-[calc(100vw-2rem)] p-0"
        side="top"
        align="center"
        sideOffset={8}
        collisionBoundary={document.body}
        collisionPadding={16}
        sticky="always"
      >
        <div className="flex flex-col">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <h3 className="text-2xs text-white">
              {t('project_selector.title')}
            </h3>
            <button type="button" onClick={handleClose}>
              <Cross2Icon className="size-4 stroke-white" />
            </button>
          </div>

          <div className="m-4 rounded-xs bg-light-container-alt">
            {isEditMode && isProjectSelected ? (
              <div className="border-b border-white/10 px-4 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-1 items-center gap-3">
                    <Avatar className="size-10">
                      <AvatarFallback>
                        {getInitials(tempSelectedProject?.name ?? '')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-1 flex-col">
                      <span className="font-semibold text-white">
                        {tempSelectedProject?.name ?? ''}
                      </span>
                      <span className="text-3xs text-primary-shade-grey-500">
                        {tCategories(
                          `${tempSelectedProject?.type === 'project' ? 'Level1' : 'Level2'}.${tempSelectedProject?.category ?? ''}` as any,
                        )}
                      </span>
                    </div>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={handleActiveChange}
                  />
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-white">
                      {t('project_selector.assign_project')}
                    </span>
                    <span className="text-3xs text-primary-shade-grey-500">
                      {t('project_selector.assign_project_description')}
                    </span>
                  </div>
                  <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-white" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('project_selector.find_project')}
                      className="rounded-xs bg-white/5 pl-10 text-2xs text-white placeholder:text-primary-shade-grey-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {!isEditMode && <div className="border-t border-white/10" />}
            <div className="flex max-h-[200px] flex-col overflow-y-auto">
              <div className="px-4 pb-2 pt-4">
                <span className="text-3xs text-primary-shade-grey-500">
                  {t('project_selector.all_projects')}
                </span>
              </div>
              <div className="flex flex-col">
                {filteredProjects.map((project) => {
                  const isSelected = tempSelectedProjectId === project.id;
                  return (
                    <button
                      key={project.id}
                      onClick={() => setTempSelectedProjectId(project.id)}
                      className="flex items-center justify-between px-4 py-3 text-left text-white hover:bg-white/5"
                    >
                      <span className="text-sm font-medium">
                        {project.name}
                      </span>
                      {isSelected && (
                        <Check className="text-custom-green-glow size-4" />
                      )}
                    </button>
                  );
                })}
                {filteredProjects.length === 0 && (
                  <div className="px-4 py-3 text-sm text-white/50">
                    {t('project_selector.no_projects')}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 border-white/10 px-4 py-3">
            {isEditMode ? (
              <>
                <Button
                  variant="outline"
                  btnType="secondary"
                  onClick={handleReset}
                  className="flex-1 text-white"
                >
                  {t('project_selector.reset')}
                </Button>
                <Button
                  variant="greenGradient"
                  btnType="secondary"
                  onClick={handleApply}
                  disabled={!hasChanges}
                  className="flex-1"
                >
                  {t('project_selector.apply')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  btnType="secondary"
                  onClick={handleClose}
                  className="flex-1 text-white"
                >
                  {t('project_selector.cancel')}
                </Button>
                <Button
                  variant="greenGradient"
                  btnType="secondary"
                  onClick={handleApply}
                  disabled={!tempSelectedProjectId}
                  className="flex-1"
                >
                  {t('project_selector.apply')}
                </Button>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
      {child}
    </Popover>
  );
};
